/**
 * `useOverlayDismiss` — Escape closes, and focus goes back where it came from.
 *
 * Parity card **P-013**: round 6h's sweep opened an overlay, pressed Escape, and found
 * `document.activeElement` on `<body>`. A keyboard user is then returned to the top of the
 * document and a screen-reader user loses their place. A second symptom of the same gap: the
 * account *Invite* dialog ignored Escape altogether.
 *
 * Round **10p** (card **P-260**) turned the hook into the product's *only* focus-return
 * mechanism. `AnchoredPopover` and the board menus used to own copies of it and each copy was
 * broken in its own way — a submenu restored focus to the row its own Escape had unmounted, a
 * dialog hoisted out of a menu captured `<body>` as its opener. The mechanism itself now lives
 * in `overlayFocus.ts` (the opener hand-off and the overlay stack) and this hook is how a React
 * surface uses it. Nothing in `packages/ui` or `apps/web` may hand-roll another one.
 *
 * What it does, while `open`:
 *   - captures the element that opened the overlay — an explicit `opener` when the surface knows
 *     its trigger, otherwise the focused element, otherwise the opener handed over by the
 *     overlay that closed in the same commit (the hoisted-dialog case) — and restores it on
 *     close, including unmount;
 *   - closes on Escape, listened for on the document so it works whether focus is inside the
 *     overlay, on the trigger, or nowhere — and only when this overlay is the **innermost** one
 *     open, so a submenu's Escape closes the submenu and not the tree.
 *
 * What it deliberately does not do: trap focus, or move focus into the overlay. Autofocus is
 * the surface's own decision (`<input autoFocus>`), and a focus trap is a bigger contract than
 * this card.
 */
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { captureOpener, handOffOpener, isTopOverlay, popOverlay, pushOverlay, releaseOpener, returnFocusTo, } from './overlayFocus.js';
/**
 * Returns the captured opener as a ref, for the surfaces that need it while open —
 * `Menu` anchors itself to it when the caller passed no coordinate (P-007).
 */
export function useOverlayDismiss({ open = true, onClose, closeOnEscape = true, returnFocus = true, opener: explicitOpener = null, captureEscape = false, }) {
    /** The latest `onClose`, so the Escape listener is installed once per open, not per render. */
    const close = useRef(onClose);
    close.current = onClose;
    const opener = useRef(null);
    /** This overlay's place on the stack; identity only, never rendered. */
    const token = useMemo(() => Symbol('overlay'), []);
    const explicit = useRef(explicitOpener);
    explicit.current = explicitOpener;
    /*
     * A **layout** effect: it runs inside the commit that mounted the overlay, before any passive
     * effect has had the chance to move focus, and before the `setTimeout(…, 0)` that expires the
     * hand-off left by a menu unmounting in this same commit.
     */
    useLayoutEffect(() => {
        if (!open || typeof document === 'undefined')
            return;
        opener.current = captureOpener(explicit.current);
        pushOverlay(token);
        return () => {
            popOverlay(token);
            const target = opener.current;
            opener.current = null;
            if (!returnFocus)
                return;
            // Own claim first (this overlay may itself have inherited the opener), then publish it:
            // a dialog this overlay is hoisting opens in this same commit and must inherit the
            // trigger rather than find `<body>`.
            releaseOpener(target);
            handOffOpener(target);
            returnFocusTo(target);
        };
    }, [open, returnFocus, token]);
    useEffect(() => {
        if (!open || !closeOnEscape || typeof document === 'undefined')
            return;
        const onKeyDown = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented)
                return;
            // Only the innermost overlay acts: a flyout closes itself, never its parent as well.
            if (!isTopOverlay(token))
                return;
            event.preventDefault();
            event.stopPropagation();
            close.current();
        };
        document.addEventListener('keydown', onKeyDown, captureEscape);
        return () => document.removeEventListener('keydown', onKeyDown, captureEscape);
    }, [open, closeOnEscape, captureEscape, token]);
    return opener;
}
//# sourceMappingURL=useOverlayDismiss.js.map